import { ApplicantionFromToken, HouseholdMember } from '../models/doc-upload.model';

// What GET /application/applicant may send. The API live during the rollout returns the WHOLE
// application under `application` — every co-applicant's email, phone and link token, reports,
// income — and userDetails is persisted to sessionStorage, so it all landed there. The new API
// trims it server-side; this keeps the app safe against either.
interface RawMember {
  _id?: unknown;
  name?: unknown;
  role?: unknown;
}

interface RawApplicantData {
  fullName?: unknown;
  unitNumber?: unknown;
  email?: unknown;
  phoneNumber?: unknown;
  propertyName?: unknown;
  application_id?: unknown;
  applicant_id?: unknown;
  role?: unknown;
  enable_doc_upload?: unknown;
  household?: unknown;
  application?: { property_name?: unknown; unit?: unknown; applicants?: unknown } | null;
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown): string => (v == null ? '' : String(v));
const optionalText = (v: unknown): string | undefined => (v == null ? undefined : String(v));

const toMember = (member: RawMember): HouseholdMember => ({ _id: text(member._id), name: text(member.name), role: text(member.role) });

/**
 * Pure: exactly the fields the app reads from an applicant response, and nothing else — so
 * nobody else's contact details or token can reach the store (or sessionStorage). Null when
 * there is no applicant object to read.
 */
export const toUserDetails = (data: unknown): ApplicantionFromToken | null => {
  if (!isObject(data)) return null;
  const raw = data as RawApplicantData;
  const application = isObject(raw.application) ? raw.application : null;

  const members = Array.isArray(raw.household) ? raw.household : Array.isArray(application?.applicants) ? application!.applicants : [];
  const household = (members as unknown[]).filter(isObject).map(toMember);

  return {
    fullName: text(raw.fullName),
    unitNumber: text(raw.unitNumber),
    email: text(raw.email),
    phoneNumber: optionalText(raw.phoneNumber),
    propertyName: text(raw.propertyName),
    application_id: text(raw.application_id),
    applicant_id: optionalText(raw.applicant_id),
    role: text(raw.role),
    enable_doc_upload: !!raw.enable_doc_upload,
    household,
    application: {
      application_id: text(raw.application_id),
      property_name: text(application?.property_name ?? raw.propertyName),
      unit: text(application?.unit ?? raw.unitNumber),
      applicants: household,
    },
  };
};
